import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  SelectDropdown,
  SearchableSelectDropdown,
  FilterBar as SharedFilterBar,
  DropdownContainer,
  ClearAllButton,
} from "components/shared/FilterDropdowns/FilterDropdowns";
import { colors } from "utils";
import { logError, LogCategory } from "utils/sentryLogger";
import { fetchSchools, fetchTeachers } from "./schoolReportsApi";
import { DATE_RANGE_OPTIONS, DEFAULT_DATE_MODE } from "./DateRangeSelect";

export const VIEW_BY_OPTIONS = [
  { value: "school", label: "School" },
  { value: "teacher", label: "Teacher" },
  { value: "student", label: "Student" },
];

export const TREND_BY_OPTIONS = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "school_year", label: "School Year" },
];

const GetReportButton = styled.button`
  height: 40px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background-color: ${colors.blue500};
  color: ${colors.gray50};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  align-self: flex-end;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CustomDates = styled(DropdownContainer)`
  display: flex;
  gap: 12px;
  flex: 0 0 auto;
`;

const DateField = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateLabel = styled.label`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.nightlyWoods950};
  margin-bottom: 6px;
  white-space: nowrap;
`;

const DateInput = styled.input`
  height: 40px;
  padding: 8px 12px;
  border: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  border-radius: 6px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods950};
  background: ${colors.white};
`;

const formatShortDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

/**
 * Shared filters above the report tabs, in the Session Reports style:
 * School (district admins only), Teacher (only teachers with data in the
 * selected range), Date Range (Session Reports presets plus Custom), View By
 * (the grouping every report is computed at) and Trend By (the period of the
 * trend reports; hidden on the non-periodic Summary tab).
 */
export const SchoolFiltersBar = ({
  scope,
  schoolId,
  teacherId,
  dateMode,
  customStart,
  customEnd,
  startDate,
  endDate,
  viewBy,
  trendBy,
  showTrendBy,
  onSchoolChange,
  onTeacherChange,
  onDateModeChange,
  onCustomStartChange,
  onCustomEndChange,
  onViewByChange,
  onTrendByChange,
  canGetReport,
  onGetReport,
}) => {
  const isDistrict = scope?.role === "district_admin";
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    if (!isDistrict) return undefined;
    let cancelled = false;
    fetchSchools()
      .then((rows) => {
        if (!cancelled) {
          setSchools((rows || []).map((s) => ({ value: s.id, label: s.name })));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          logError("School Reports: schools filter fetch failed", e, {
            category: LogCategory.ERROR,
          });
        }
      });
    return () => { cancelled = true; };
  }, [isDistrict]);

  useEffect(() => {
    if (!startDate || !endDate) return undefined;
    let cancelled = false;
    fetchTeachers({ schoolId: schoolId ?? undefined, startDate, endDate })
      .then((rows) => {
        if (cancelled) return;
        setTeachers(
          (rows || []).map((t) => ({ value: t.id, label: t.name || t.email }))
        );
      })
      .catch((e) => {
        if (!cancelled) {
          logError("School Reports: teachers filter fetch failed", e, {
            category: LogCategory.ERROR,
          });
        }
      });
    return () => { cancelled = true; };
  }, [schoolId, startDate, endDate]);

  // Validation runs against the CURRENT selection whenever either side
  // changes — never from the fetch's closure, so a teacher picked while a
  // fetch is in flight can't be clobbered by a stale value. A teacher with
  // no data in the new range is dropped so the reports and dropdown agree.
  useEffect(() => {
    if (teacherId && !teachers.some((o) => o.value === teacherId)) {
      onTeacherChange(null);
    }
  }, [teachers, teacherId, onTeacherChange]);

  const dateDisplayText = (() => {
    if (dateMode === "custom") {
      if (customStart && customEnd) {
        return `${formatShortDate(customStart)} – ${formatShortDate(customEnd)}`;
      }
      return "Custom";
    }
    return (
      DATE_RANGE_OPTIONS.find((o) => o.value === dateMode)?.label ||
      "This School Year"
    );
  })();

  // A school admin's whole scope is one school, so grouping by school would
  // return one row. The backend accepts it anyway; the option is just hidden.
  const viewByOptions = isDistrict
    ? VIEW_BY_OPTIONS
    : VIEW_BY_OPTIONS.filter((o) => o.value !== "school");

  const handleClearAll = () => {
    onSchoolChange(null);
    onTeacherChange(null);
    onDateModeChange(DEFAULT_DATE_MODE);
    onCustomStartChange("");
    onCustomEndChange("");
    onViewByChange("teacher");
    onTrendByChange("week");
  };

  return (
    <SharedFilterBar>
      <SelectDropdown
        label="View By"
        value={viewBy}
        displayText={viewByOptions.find((o) => o.value === viewBy)?.label || "Teacher"}
        options={viewByOptions}
        onChange={onViewByChange}
        clearable
        clearValue="teacher"
      />
      {isDistrict && (
        <SelectDropdown
          label="School"
          value={schoolId}
          displayText={
            schools.find((s) => s.value === schoolId)?.label || "All Schools"
          }
          options={[{ value: null, label: "All Schools" }, ...schools]}
          onChange={onSchoolChange}
          clearable
        />
      )}
      <SearchableSelectDropdown
        label="Teacher"
        value={teacherId}
        displayText={
          teachers.find((t) => t.value === teacherId)?.label || "All Teachers"
        }
        options={[{ value: null, label: "All Teachers" }, ...teachers]}
        onChange={onTeacherChange}
        clearable
      />
      <SelectDropdown
        label="Date Range"
        value={dateMode}
        displayText={dateDisplayText}
        options={DATE_RANGE_OPTIONS}
        onChange={onDateModeChange}
        clearable
        clearValue={DEFAULT_DATE_MODE}
      />
      {dateMode === "custom" && (
        <CustomDates>
          <DateField>
            <DateLabel htmlFor="school-reports-custom-start">Start date</DateLabel>
            <DateInput
              id="school-reports-custom-start"
              type="date"
              value={customStart || ""}
              onChange={(e) => onCustomStartChange(e.target.value)}
            />
          </DateField>
          <DateField>
            <DateLabel htmlFor="school-reports-custom-end">End date</DateLabel>
            <DateInput
              id="school-reports-custom-end"
              type="date"
              value={customEnd || ""}
              onChange={(e) => onCustomEndChange(e.target.value)}
            />
          </DateField>
        </CustomDates>
      )}
      {showTrendBy && (
        <SelectDropdown
          label="Trend By"
          value={trendBy}
          displayText={TREND_BY_OPTIONS.find((o) => o.value === trendBy)?.label || "Week"}
          options={TREND_BY_OPTIONS}
          onChange={onTrendByChange}
          clearable
          clearValue="week"
        />
      )}
      <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>
      <GetReportButton type="button" onClick={onGetReport} disabled={!canGetReport}>
        Get Report
      </GetReportButton>
    </SharedFilterBar>
  );
};
